import { useEffect, useState } from "react";
import { Lightbulb, Puzzle, Repeat, Rocket, Users } from "lucide-react";

const ICONS = { s: Users, e: Rocket, c: Puzzle, i: Lightbulb };

/** Full literals so Tailwind sees every class. Every step shares one soft style; the travelling arc is the orange highlighter. */
const STYLES = [
  {
    nodeOn: "border-brand-500 bg-brand-500 text-white",
    nodeOff: "border-brand-500 bg-white text-brand-500",
    panel: "border-brand-200 bg-brand-100 text-ink",
    icon: "bg-white text-brand-600",
    text: "text-ink/70",
    letter: "text-brand-500",
    ring: "border-brand-200 bg-brand-50",
  },
  {
    nodeOn: "border-brand-500 bg-brand-500 text-white",
    nodeOff: "border-brand-500 bg-white text-brand-500",
    panel: "border-brand-200 bg-brand-100 text-ink",
    icon: "bg-white text-brand-600",
    text: "text-ink/70",
    letter: "text-brand-500",
    ring: "border-brand-200 bg-brand-50",
  },
  {
    nodeOn: "border-brand-500 bg-brand-500 text-white",
    nodeOff: "border-brand-500 bg-white text-brand-500",
    panel: "border-brand-200 bg-brand-100 text-ink",
    icon: "bg-white text-brand-600",
    text: "text-ink/70",
    letter: "text-brand-500",
    ring: "border-brand-200 bg-brand-50",
  },
  {
    nodeOn: "border-brand-500 bg-brand-500 text-white",
    nodeOff: "border-brand-500 bg-white text-brand-500",
    panel: "border-brand-200 bg-brand-100 text-ink",
    icon: "bg-white text-brand-600",
    text: "text-ink/70",
    letter: "text-brand-500",
    ring: "border-brand-200 bg-brand-50",
  },
];

/** Node positions on the ring (percent of the box): top, right, bottom, left. */
const NODE_POSITIONS = [
  { left: "50%", top: "12%" },
  { left: "88%", top: "50%" },
  { left: "50%", top: "88%" },
  { left: "12%", top: "50%" },
];

const RING_RADIUS = 38; // in a 100x100 viewBox, matches the node positions above
const QUARTER = (2 * Math.PI * RING_RADIUS) / 4;

function prefersReducedMotion() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * The SECI learning cycle as a real loop: four steps around a ring, an orange comet travelling from step to step
 * (S, E, C, I, then round again), and the active step explained next to it.
 * It pauses while the visitor hovers or focuses it; tap a letter to jump to that step.
 * With reduced motion it stays on the chosen step. Every step's text stays in the DOM (hidden attribute) for crawlers.
 */
export function SeciCycle({ steps, loopLabel }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = steps.length;

  useEffect(() => {
    if (paused || prefersReducedMotion()) return undefined;
    const id = window.setInterval(() => setActive((current) => (current + 1) % count), 3400);
    return () => window.clearInterval(id);
  }, [paused, count]);

  const style = STYLES[active % STYLES.length];

  return (
    <div
      className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14"
      // Pause for a mouse hover or keyboard focus only; on touch a tap must not freeze it for good.
      onPointerEnter={(e) => e.pointerType === "mouse" && setPaused(true)}
      onPointerLeave={(e) => e.pointerType === "mouse" && setPaused(false)}
      onFocus={(e) => e.target.matches(":focus-visible") && setPaused(true)}
      onBlur={() => setPaused(false)}
      data-testid="home-seci"
    >
      {/* The ring */}
      <div className="relative mx-auto aspect-square w-full max-w-[19rem] sm:max-w-sm">
        {/* Slowly turning dashed track */}
        <svg viewBox="0 0 100 100" aria-hidden="true" className="absolute inset-0 h-full w-full animate-spin" style={{ animationDuration: "60s" }}>
          <circle cx="50" cy="50" r={RING_RADIUS} fill="none" strokeWidth="0.65" strokeDasharray="0.7 2.6" strokeLinecap="round" className="stroke-brand-300" />
        </svg>
        {/* The comet: a bright arc from the current step to the next one */}
        <svg
          viewBox="0 0 100 100"
          aria-hidden="true"
          className="absolute inset-0 h-full w-full transition-transform duration-[900ms] ease-in-out"
          style={{ transform: `rotate(${-90 + active * 90}deg)` }}
        >
          <circle
            cx="50"
            cy="50"
            r={RING_RADIUS}
            fill="none"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeDasharray={`${QUARTER * 0.82} 400`}
            className="stroke-sun-400"
          />
        </svg>

        {/* Centre: the big active letter */}
        <div
          className={`absolute inset-[27%] flex flex-col items-center justify-center rounded-full border-2 transition-colors duration-500 ${style.ring}`}
          aria-hidden="true"
        >
          <span key={active} className={`animate-pop font-heading text-6xl font-bold leading-none sm:text-7xl ${style.letter}`}>
            {steps[active].letter}
          </span>
          <span className="mt-1 text-[0.7rem] font-bold uppercase tracking-wider text-ink/60 sm:text-xs">
            Step {active + 1} of {count}
          </span>
        </div>

        {steps.map((step, i) => {
          const s = STYLES[i % STYLES.length];
          const on = i === active;
          return (
            <button
              key={step.id}
              type="button"
              onClick={() => setActive(i)}
              aria-pressed={on}
              aria-label={`Show step ${step.letter}: ${step.title}`}
              style={NODE_POSITIONS[i]}
              className={`absolute z-10 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 font-heading text-2xl font-bold transition-all duration-300 sm:h-[4.5rem] sm:w-[4.5rem] sm:text-3xl ${
                on ? `${s.nodeOn} scale-110 shadow-soft` : `${s.nodeOff} hover:scale-105`
              }`}
            >
              {step.letter}
            </button>
          );
        })}
      </div>

      {/* The active step, explained */}
      <div>
        <ol className="relative">
          {steps.map((step, i) => {
            const s = STYLES[i % STYLES.length];
            const Icon = ICONS[step.id] ?? Users;
            return (
              <li
                key={step.id}
                hidden={i !== active}
                className={`animate-pop rounded-4xl border p-6 shadow-soft sm:p-8 ${s.panel}`}
              >
                <span aria-hidden="true" className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl ${s.icon}`}>
                  <Icon className="h-6 w-6" />
                </span>
                <h4 className="font-heading text-3xl font-bold leading-tight sm:text-4xl">
                  <span aria-hidden="true" className="mr-2 opacity-60">
                    {step.letter}
                  </span>
                  {step.title}
                </h4>
                <p className={`mt-2 text-base sm:text-lg ${s.text}`}>{step.text}</p>
              </li>
            );
          })}
        </ol>

        <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 py-1.5 pl-1.5 pr-4 text-sm font-bold sm:text-base">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-500 text-white" aria-hidden="true">
            <Repeat className="h-4 w-4" />
          </span>
          {loopLabel}
        </p>
      </div>
    </div>
  );
}
